import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Loader } from "@mantine/core";

type Props = {
    value: string;
    onChange: (val: string) => void;
    onSelect?: (payload: { description: string; lat: number; lng: number }) => void;
    placeholder?: string;
    country?: string; // Ej: 'co' para Colombia
    disabled?: boolean;
    delay?: number; // tiempo de espera (ms) tras dejar de escribir
    maxResults?: number; // límite de resultados mostrados
} & Record<string, any>;

const loadGoogleScript = (key: string) => {
    return new Promise<void>((resolve, reject) => {
        if (window.google?.maps?.Geocoder) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[data-google-maps]`);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", () =>
                reject(new Error("Google Maps script failed to load"))
            );
            return;
        }
        const script = document.createElement("script");
        script.setAttribute("data-google-maps", "true");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () =>
            reject(new Error("Google Maps script failed to load"));
        document.head.appendChild(script);
    });
};

const GoogleGeocodeAutocomplete: React.FC<Props> = ({
    value,
    onChange,
    onSelect,
    placeholder = "Escribe una dirección",
    country,
    disabled,
    delay = 1000,
    maxResults = 5,
    ...rest
}) => {
    const GOOGLE_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string;
    const [opciones, setOpciones] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
    const timerRef = useRef<number>(0);

    const resultadosCache = useRef<Map<string, { lat: number; lng: number }>>(new Map());


    useEffect(() => {
        loadGoogleScript(GOOGLE_KEY)
            .then(() => {
                setGeocoder(new google.maps.Geocoder());
            })
            .catch(() => { });
    }, []);

    const [isTyping, setIsTyping] = useState(false);

    useEffect(() => {
        if (!geocoder || !value || value.trim().length < 3) {
            setOpciones([]);
            return;
        }

        // Si el cambio no fue por escritura, ignorar
        if (!isTyping) {
            return;
        }


        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => {
            setLoading(true);
            const request: google.maps.GeocoderRequest = { address: value };
            if (country)
                request.componentRestrictions = { country: country.toLowerCase() };

            geocoder.geocode(request, (results, status) => {
                setLoading(false);

                if (status !== "OK" || !results) {
                    setOpciones([]);
                    return;
                }

                const sliced = results.slice(0, maxResults);

                // Limpiar cache y guardar solo las opciones que se mostrarán
                resultadosCache.current.clear();
                const opcionesFiltradas = sliced
                    .filter(r => r.geometry?.location) // solo los que tienen coordenadas
                    .map(r => ({
                        address: r.formatted_address,
                        location: {
                            lat: r.geometry.location.lat(),
                            lng: r.geometry.location.lng()
                        }
                    }));

                // Actualizar cache y opciones en sincronía
                opcionesFiltradas.forEach(r => {
                    resultadosCache.current.set(r.address, r.location);
                });
                setOpciones(opcionesFiltradas.map(r => r.address));

                // ✅ Si solo hay un resultado, usarlo automáticamente (solo setear coordenadas)
                if (sliced.length === 1) {
                    const unico = sliced[0];
                    const loc = unico.geometry?.location;
                    if (loc) {
                        onSelect?.({
                            description: value, // mantener texto original
                            lat: loc.lat(),
                            lng: loc.lng(),
                        });
                    }
                }
            });
        }, delay);

        return () => window.clearTimeout(timerRef.current);
    }, [value, geocoder, country, delay, maxResults, isTyping]);

    // ✅ Usar SOLO el cache para selecciones, sin fallback a Google
    const handleSelect = (direccion: string) => {
        onChange(direccion);

        // Solo usar el cache, ya que las opciones mostradas SIEMPRE deberían estar en cache
        const cached = resultadosCache.current.get(direccion);
        if (cached) {
            onSelect?.({
                description: direccion,
                lat: cached.lat,
                lng: cached.lng,
            });
        } else {
            console.warn('⚠️ Dirección seleccionada no encontrada en cache:', direccion);
        }
    };

    return (
        <Autocomplete
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            data={opciones}
            onChange={(val) => {
                const isSelecting = opciones.includes(val);
                setIsTyping(!isSelecting);

                if (isSelecting) {
                    handleSelect(val);
                } else {
                    onChange(val);
                }
            }}
            rightSection={loading ? <Loader size="xs" /> : null}
            comboboxProps={{ zIndex: 9999 }}
            filter={({ options }) => options}
            title={value.length > 40 ? value : undefined}
            {...rest}
        />
    );
};

export default GoogleGeocodeAutocomplete;
